<template>
  <s-modal
    v-model="showModal"
    class="details-alarm-modal"
    size="md"
    :ok-title="okTitle"
    :cancel-title="$t('actions.close')"
    :ok-only="okOnly"
    :ok-disabled="okDisabled"
    @cancel="cancel"
    @ok="getOkAction"
    :title="modalTitle"
  >
    <p class="details-alarm-modal__moduleName">{{ alarm.moduleName }}</p>
    <s-table :fields="fields" :items="alarm.events" :busy="loading">
      <template #cell(number)="{ index }">
        <span>
          {{ alarm.events.length - index }}
        </span>
      </template>
      <template #cell(description)="{ item, index }">
        <b-row class="align-items-center">
          <b-col class="text-left" cols="2">
            <icon-badge
              icon-class="fas fa-bell"
              badge-icon-class="fas fa-check"
              :icon-color="getAlarmColor(item)"
              :badge-color="getBadgeColor(item)"
              :show-badge="
                getRecognizedEvent(item) ||
                  isEventNormalizedAfterRecognized(item, index) ||
                  isIgnored(item)
              "
            />
          </b-col>
          <b-col>
            <b-row
              :style="{
                color: getLabelColor(item, index),
                textTransform: 'uppercase',
              }"
              class="align-items-center pl-1"
            >
              <strong class="mr-1">
                {{ getEventStatusLabel(item, index) }}
              </strong>
              <strong v-if="getRecognizedEvent(item, index)">
                {{ $t('alarms-and-warnings.events.by') }}
                {{ item.recognizedUserName }}</strong
              >
            </b-row>
            <b-row class="pl-1">
              {{ getFormattedDateTime(item.dateAndTime) }}
            </b-row>
          </b-col>
        </b-row>
      </template>
      <template #cell(recurrence)="{ item, index }">
        <strong
          :style="{
            color: getLabelColor(item, index),
          }"
          >{{ getRecurrence(item, index) }}</strong
        >
      </template>
    </s-table>
    <b-container v-if="hasBatch">
      <b-row class="d-flex justify-content-between px-2">
        <p>
          <strong>{{ $t('alarms-and-warnings.events.sampleDateTime') }}</strong>
          <span>
            {{ getFormattedSampleDateTime(alarm.events[0].sampleDateTime) }}</span
          >
        </p>
        <p>
          <strong>{{ $t('alarms-and-warnings.events.batch') }}</strong>
          <span>{{ alarm.events[0].batch }}</span>
        </p>
      </b-row>
    </b-container>
  </s-modal>
</template>

<script>
import translate from '@/services/translate';
import IconBadge from '@/components/icon/IconBadge';
import { AlarmEventStatusType } from 'sigma-mappings';
import { formats, timezones } from '@/constants/localizer';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';

export default {
  name: 'DetailsAlarmModal',
  components: {
    IconBadge,
  },
  props: {
    alarm: {
      type: Object,
      required: true,
    },
    okOnly: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    showModal: false,
    loading: false,
    description: null,
    waitManager: null,
    ignore: false,
    fields: [
      {
        key: 'number',
        label: translate('fields.alarm.events.number'),
      },
      {
        key: 'description',
        label: translate('fields.alarm.events.description'),
      },
      {
        key: 'recurrence',
        label: translate('fields.alarm.events.recurrence'),
      },
    ],
  }),
  computed: {
    modalTitle() {
      return translate('alarms-and-warnings.alarm-event-history');
    },
    okTitle() {
      return this.okOnly
        ? translate('actions.close')
        : translate('alarms-and-warnings.recognition.recognize');
    },
    okDisabled() {
      if (!this.$hasUserPermission('PER003')) {
        return true;
      }

      return this.okOnly === false && this.alarm.recognized;
    },
    recognized() {
      return this.alarm.recognized ? this.alarm.recognized : true;
    },
    hasBatch() {
      return this.alarm?.events?.some(item => item.batch);
    },
  },
  methods: {
    show() {
      this.showModal = true;
    },
    cancel() {
      this.showModal = false;
    },
    openRecognizeModal() {
      this.showModal = false;
      this.$emit('open-recognize-modal', this.alarm);
    },
    getAlarmColor(event) {
      switch (event.alarmEventStatus) {
        case AlarmEventStatusType.RECOGNIZED:
          return 'var(--color-secondary)';
        case AlarmEventStatusType.ACTED:
          return 'var(--color-secondary)';
        case AlarmEventStatusType.NORMALIZED:
          return '';
        default:
          return '';
      }
    },

    isEventNormalizedAfterRecognized(event, index) {
      const recognizedIndex = this.alarm.events.findIndex(
        e => e.alarmEventStatus === AlarmEventStatusType.RECOGNIZED,
      );

      if (
        (event.alarmEventStatus === AlarmEventStatusType.NORMALIZED
          || event.alarmEventStatus
            === AlarmEventStatusType.NORMALIZEDRECOGNIZED)
        && index < recognizedIndex
      ) {
        return true;
      }
      return false;
    },
    isIgnored(event) {
      return event.alarmEventStatus === AlarmEventStatusType.IGNORED;
    },
    getRecognizedEvent(event, index) {
      if (this.isEventNormalizedAfterRecognized(event, index)) {
        return false;
      }
      return (
        event.alarmEventStatus === AlarmEventStatusType.RECOGNIZED
        || event.alarmEventStatus === AlarmEventStatusType.NORMALIZEDRECOGNIZED
      );
    },
    getRecurrence(event, index) {
      if (this.isEventNormalizedAfterRecognized(event, index)) {
        return event.recurrence;
      }
      if (
        event.alarmEventStatus === AlarmEventStatusType.NORMALIZEDRECOGNIZED
      ) {
        return '-';
      }
      if (event.alarmEventStatus === AlarmEventStatusType.RECOGNIZED) {
        return '-';
      }
      return event.recurrence;
    },
    getBadgeColor(event) {
      switch (event.alarmEventStatus) {
        case AlarmEventStatusType.RECOGNIZED:
          return 'var(--color-info)';
        case AlarmEventStatusType.ACTED:
          return 'var(--color-secondary)';
        case AlarmEventStatusType.NORMALIZED:
        case AlarmEventStatusType.NORMALIZEDRECOGNIZED:
          return '';
        default:
          return '';
      }
    },
    getLabelColor(event, index) {
      if (this.isEventNormalizedAfterRecognized(event, index)) {
        return '';
      }
      switch (event.alarmEventStatus) {
        case AlarmEventStatusType.RECOGNIZED:
        case AlarmEventStatusType.NORMALIZEDRECOGNIZED:
          return 'var(--color-info)';
        case AlarmEventStatusType.ACTED:
          return 'var(--color-secondary)';
        case AlarmEventStatusType.NORMALIZED:
          return '';
        default:
          return '';
      }
    },
    getEventStatusLabel(event, index) {
      if (this.isEventNormalizedAfterRecognized(event, index)) {
        return translate('alarms-and-warnings.events.normalized');
      }
      switch (event.alarmEventStatus) {
        case AlarmEventStatusType.RECOGNIZED:
        case AlarmEventStatusType.NORMALIZEDRECOGNIZED:
          return translate('alarms-and-warnings.events.recognized');
        case AlarmEventStatusType.ACTED:
          return translate('alarms-and-warnings.events.acted');
        case AlarmEventStatusType.NORMALIZED:
          return translate('alarms-and-warnings.events.normalized');
        case AlarmEventStatusType.IGNORED:
          return translate('alarms-and-warnings.events.ignored');
        default:
          return '';
      }
    },
    getFormattedDateTime(date, type = 'default') {
      const typeFormatDateTime = {
        date: formats.DATE_SHORT,
        hour: formats.TIME_24_SIMPLE,
        default: formats.DATETIME_SHORT,
      };

      const findTypeOfDate = Object.keys(typeFormatDateTime).find(key => type === key) || 'default';
      const keyTypeDate = typeFormatDateTime[findTypeOfDate];

      return dateTimeFormatter(date, keyTypeDate);
    },
    getFormattedSampleDateTime(date) {
      return dateTimeFormatter(
        date,
        formats.DATETIME_SHORT_WITH_SECONDS,
        timezones.UTC,
        '-',
      );
    },
    getOkAction() {
      if (this.okOnly) {
        this.cancel();
      } else {
        this.openRecognizeModal();
      }
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.details-alarm-modal
  width: 550px
  &__title
    font-weight: bold
  &__moduleName
    font-size: 1.5em
    font-weight: normal
  &__alarm-data-title
    color: var(--color-primary)
  &__info-container
    margin-bottom: 5px
    &_horizontal
      display: flex
      flex-direction: column
  .statusTable
    padding: 10px !important
    text-align: center

.s-table
    max-height: 60vh
    border-radius: 5px !important

    ::v-deep thead
      position: sticky
      top: 0
      background-color: var(--color-table-header-bg)
      z-index: 2

    ::v-deep
      th, td
        text-align: left !important
        border: 3px solid var(--color-background-light) !important

      tr:first-child th
        border-top: none !important
      tr:last-child td
        border-bottom: none !important
      th:first-child, td:first-child
        border-left: none !important
      th:last-child, td:last-child
        border-right: none !important

      th:nth-child(1),
      td:nth-child(1)
        text-align: center !important
      th:nth-child(2),
      td:nth-child(2)
        text-align: left !important
      th:nth-child(3),
      td:nth-child(3)
        text-align: center !important
</style>
